<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Paperclip, ChevronDown } from 'lucide-vue-next';
import { request } from '@/lib/api';
import { $t } from '@/i18n';
import Tooltip2 from '@/components/Tooltip2.vue';
import AddImageList from '@/components/create/AddImageList.vue';
import AttachPanel from '@/components/preview/chat/AttachPanel.vue';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/toast';
import { useAttachmentUpload } from '@/composables/useAttachmentUpload';

const { toast } = useToast();

const props = defineProps({
  to: Object,
  class: String,
  isReadOnly: Boolean,
  authCheck: Function,
  editorState: Object,
  open: Boolean,
});

const emit = defineEmits(['change', 'update:open']);

// State
const activePanel = ref(null);
const showPreview = ref(false);

// Composable
const { isUploading, uploadProgress, uploadReference, uploadMaterial, uploadDocument } =
  useAttachmentUpload();

const attachmentPopoverOpen = computed({
  get: () => (props.open !== undefined ? props.open : props.editorState.attach),
  set: (v) => {
    if (props.open !== undefined) {
      emit('update:open', v);
    } else {
      props.editorState.attach = v;
    }
  },
});

const activeTab = computed({
  get: () => props.editorState.attachActiveTab || 'reference',
  set: (v) => (props.editorState.attachActiveTab = v),
});

const totalAttachments = computed(() => {
  let count = 0;
  if (props.editorState.imagePreview) count += 1;
  if (props.editorState.assets?.length) count += props.editorState.assets.length;
  if (props.editorState.documents?.length) count += props.editorState.documents.length;
  return count;
});

const isAnyUploading = computed(() => {
  return isUploading.value.reference || isUploading.value.material || isUploading.value.document;
});

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleKeyboardShortcuts);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyboardShortcuts);
});

// Helpers
function authCheck() {
  if (props.authCheck) return props.authCheck();
  return false;
}

function announceToScreenReader(message) {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', 'polite');
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  document.body.appendChild(announcement);

  toast({ title: message });

  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

function flushAssetList() {
  activePanel.value?.flush();
}

// Upload Handlers
async function handleReferenceUpload(files) {
  if (!files?.length) return;
  if (authCheck()) return;

  await uploadReference(files[0], {
    onSuccess: (payload) => {
      emit('change', payload);
      announceToScreenReader($t('attach.uploadSuccessful'));
      flushAssetList();
      attachmentPopoverOpen.value = false;
    },
    onError: (msg) => announceToScreenReader(msg || $t('attach.uploadFailed')),
  });
}

function handleReferencePick(asset) {
  emit('change', asset);
}

async function handleMaterialUpload(files) {
  if (!files?.length) return;
  if (authCheck()) return;

  await uploadMaterial(files, {
    onPick: handleMaterialPick,
    onSuccess: () => {
      announceToScreenReader($t('attach.uploadSuccessful'));
      flushAssetList();
    },
    onError: (msg) => announceToScreenReader(msg || $t('attach.uploadFailed')),
  });
}

function handleMaterialPick(asset) {
  if (!props.editorState.assets) {
    props.editorState.assets = [];
  }
  props.editorState.assets.push(asset);
}

async function handleDocumentUpload(files) {
  if (!files?.length) return;
  if (authCheck()) return;

  await uploadDocument(files, {
    onPick: handleDocumentPick,
    onSuccess: () => {
      announceToScreenReader($t('attach.uploadSuccessful'));
      flushAssetList();
    },
    onError: (msg) => announceToScreenReader(msg || $t('attach.uploadFailed')),
  });
}

function handleDocumentPick(asset) {
  if (!props.editorState.documents) {
    props.editorState.documents = [];
  }
  asset.parsed = !!asset.parsed_key;

  const exists = props.editorState.documents.some((doc) => doc.id === asset.id);
  if (!exists) {
    props.editorState.documents.push(asset);
  }

  if (!asset.parsed) {
    request(`/asset/${asset.id}/parse`, { method: 'POST' })
      .then(() => updateDocumentParsedStatus(asset.id, true))
      .catch(() => updateDocumentParsedStatus(asset.id, true));
  }
}

function updateDocumentParsedStatus(assetId, parsed) {
  const docIndex = props.editorState.documents.findIndex((doc) => doc.id === assetId);
  if (docIndex !== -1) {
    props.editorState.documents[docIndex] = {
      ...props.editorState.documents[docIndex],
      parsed,
    };
  }
}

// Keyboard
function handleKeyboardShortcuts(event) {
  if ((event.ctrlKey || event.metaKey) && event.key === 'u') {
    event.preventDefault();
    attachmentPopoverOpen.value = true;
    return;
  }
  if (event.key === 'Escape') {
    attachmentPopoverOpen.value = false;
    showPreview.value = false;
    return;
  }
}
</script>

<template>
  <Tooltip2 :text="$t('create.attachTips')">
    <Popover v-model:open="attachmentPopoverOpen">
      <PopoverTrigger asChild>
        <button
          :disabled="isReadOnly"
          :class="
            cn(
              'group py-1 select-none px-1 bg-white rounded-full text-black/60 transition-colors flex items-center gap-0.5 cursor-pointer border border-black/5 hover:bg-black/5 hover:border-black/20 data-[state=open]:bg-black/5 data-[state=open]:border-black/20',
              props.class,
            )
          "
          :aria-label="`${$t('attach.attachmentManager')} (${totalAttachments} ${$t(
            'attach.attachments',
          )})`"
        >
          <div class="shrink-0 size-6 flex items-center justify-center">
            <div
              v-if="isAnyUploading"
              class="size-3.5 border-2 border-black/20 border-t-black/60 rounded-full animate-spin"
            ></div>
            <Paperclip v-else class="size-3.5 text-black/60" />
          </div>
          <ChevronDown
            class="size-3.5 text-black/30 group-hover:text-black/90 transition-colors shrink-0"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        :to="to"
        class="w-[420px] p-0 rounded-[20px] backdrop-blur-[60px] shadow-2xl border-black/5 bg-white/85"
        align="start"
        side="right"
        :prioritizePosition="true"
        :sideOffset="16"
      >
        <Tabs v-model="activeTab" class="w-full p-1">
          <TabsList class="grid w-full grid-cols-3 p-1 mb-0 bg-black/5 rounded-full border-0">
            <TabsTrigger
              v-for="tab in ['reference', 'material', 'document']"
              :key="tab"
              :value="tab"
              class="flex items-center justify-center px-1 py-1 text-sm font-normal rounded-full data-[state=active]:shadow-sm text-black/60 hover:text-black/80 transition-all duration-200 data-[state=active]:bg-white"
            >
              <span class="hidden sm:inline">
                {{
                  tab === 'reference'
                    ? $t('attach.referenceImage')
                    : tab === 'material'
                      ? $t('attach.materialLibrary')
                      : $t('attach.documents')
                }}
              </span>
              <Badge
                v-if="tab === 'reference' && props.editorState.imagePreview"
                variant="secondary"
                class="ml-1 text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full"
                >1</Badge
              >
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reference" class="mt-2 p-0">
            <AttachPanel
              ref="activePanel"
              type="reference"
              :title="$t('attach.addReferenceImage')"
              :description="$t('attach.supportedFormats')"
              accept=".png,.jpg,.jpeg,.webp"
              picker-type="ref"
              :uploading="isUploading.reference"
              :progress="uploadProgress.reference"
              @upload="handleReferenceUpload"
              @pick="handleReferencePick"
            >
              <template #extra>
                <AddImageList @click="handleReferencePick" />
              </template>
            </AttachPanel>
          </TabsContent>

          <TabsContent value="material" class="mt-2 p-0">
            <AttachPanel
              ref="activePanel"
              type="material"
              :title="$t('attach.uploadImageMaterial')"
              :description="$t('attach.supportedFormats')"
              accept=".png,.jpg,.jpeg,.webp"
              :multiple="true"
              :uploading="isUploading.material"
              :progress="uploadProgress.material"
              @upload="handleMaterialUpload"
              @pick="handleMaterialPick"
            />
          </TabsContent>

          <TabsContent value="document" class="mt-2 p-0">
            <AttachPanel
              ref="activePanel"
              type="document"
              :title="$t('attach.addDocuments')"
              :description="$t('attach.supportedDocumentFormats')"
              accept=".txt,.md,.pdf,.docx"
              picker-type="document"
              :uploading="isUploading.document"
              :progress="uploadProgress.document"
              @upload="handleDocumentUpload"
              @pick="handleDocumentPick"
            />
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  </Tooltip2>
</template>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
